/**
 * The `useApi` function is a custom hook in a React application that handles API requests with
 * authentication using AWS Cognito and Axios.
 * @returns The `useApi` function returns an object containing four functions: `getAPI`, `PostAPI`,
 * `PatchAPI`, and `DeleteAPI`. These functions are used to make API requests with proper authorization
 * headers using the Cognito access token.
 */

import { fetchAuthSession } from 'aws-amplify/auth'
import axios from 'axios';
import config from '../config/config';

const useApi = () => {
    /**
     * Fetches the access token for the current authenticated user using AWS Amplify's `Auth` module.
     * @returns The access token string.
     * @throws If the user is not authenticated or the token cannot be retrieved.
     */
    const baseURL = config.baseURL;
    /**
     * Makes a GET request to the specified endpoint with authorization headers.
     * @returns An object with `error` and `data` properties.
     */
    const getAPI = async (endpoint: string, header = true, headers?: any) => {
        try {
            await fetchAuthSession()
            const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
            const defaultHeaders = { Authorization: `Bearer ${authToken}` };
            const finalHeaders = headers ? { ...defaultHeaders, ...headers } : defaultHeaders;
            let data;
            if (header) {
                data = await axios.get(`${baseURL}${endpoint}`, { headers: finalHeaders });
            } else {
                data = await axios.get(`${baseURL}${endpoint}`);
            }
            return { error: false, data: data.data };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // Handle Axios-specific error
                if (((error.message.includes('User needs to be authenticated')) || (error.name === 'UserUnAuthenticatedException') || (error.message === 'Network Error') ||
                (error.message.includes('Access Token has been revoked')) || (error.name === 'NotAuthorizedException'))) {
                    // console.log(error);
                    // window.location.reload();
                    // localStorage.clear();
                }
                return { error: true, data: error.response };
            } else {
                // Handle non-Axios errors
                return { error: true, data: 'An unexpected error occurred' };
            }
        }
    };

    /**
     * Makes a POST request to the specified endpoint with authorization headers and a payload.
     * @returns An object with `error` and `data` properties.
     */
    const PostAPI = async (endpoint: any, payload: any) => {
        try {
            await fetchAuthSession()
            const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
            const headers = { Authorization: `Bearer ${authToken}` };
            const { data } = await axios.post(`${baseURL}${endpoint}`, payload, { headers });
            return { error: false, data };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // Handle Axios-specific error
                if (((error.message.includes('User needs to be authenticated')) || (error.name === 'UserUnAuthenticatedException') || (error.message === 'Network Error') ||
                (error.message.includes('Access Token has been revoked')) || (error.name === 'NotAuthorizedException'))) {
                    // console.log(error);
                    // window.location.reload();
                    // localStorage.clear();
                }
                return { error: true, data: error };
            } else {
                // Handle non-Axios errors
                return { error: true, data: 'An unexpected error occurred' };
            }
        }
    };

    /**
     * Makes a PATCH request to the specified endpoint with authorization headers and a payload.
     * @returns An object with `error` and `data` properties.
     */
    const PatchAPI = async (endpoint: any, payload?: any, header = true, headers?: any, useXAccessToken = false) => {
        try {
            await fetchAuthSession()
            const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
            const accessToken = (await fetchAuthSession()).tokens?.accessToken?.toString();
            let defaultHeaders;
            if (useXAccessToken) {
                defaultHeaders = { 'x-access-token': accessToken,  Authorization: `Bearer ${authToken}` };
            } else {
                defaultHeaders = { Authorization: `Bearer ${authToken}` };
            }
            const finalHeaders = headers ? { ...defaultHeaders, ...headers } : defaultHeaders;
            let data;
            if (header) {
                data = await axios.patch(`${baseURL}${endpoint}`, payload, { headers: finalHeaders });
            } else {
                data = await axios.patch(`${baseURL}${endpoint}`, payload);
            }
            return { error: false, data: data.data };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (((error.message.includes('User needs to be authenticated')) || (error.name === 'UserUnAuthenticatedException') || (error.message === 'Network Error') ||
                (error.message.includes('Access Token has been revoked')) || (error.name === 'NotAuthorizedException'))) {
                    // console.log(error);
                    // window.location.reload();
                    // localStorage.clear();
                }
                // Handle Axios-specific error
                return { error: true, data: error };
            } else {
                // Handle non-Axios errors
                return { error: true, data: 'An unexpected error occurred' };
            }
        }
    };
    /**
     * Makes a PUT request to the specified endpoint with authorization headers and a payload.
     * @returns An object with `error` and `data` properties.
     */
    const PutAPI = async (endpoint: any, payload?: any, header = true) => {
        try {
            await fetchAuthSession()
            const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
            const headers = { Authorization: `Bearer ${authToken}` };
            let data;
            if (header) {
                data = await axios.put(`${baseURL}${endpoint}`, payload, { headers });
            } else {
                data = await axios.put(`${baseURL}${endpoint}`, payload);
            }
            return { error: false, data: data.data };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (((error.message.includes('User needs to be authenticated')) || (error.name === 'UserUnAuthenticatedException') || (error.message === 'Network Error') ||
                (error.message.includes('Access Token has been revoked')) || (error.name === 'NotAuthorizedException'))) {
                    // console.log(error);
                    // window.location.reload();
                    // localStorage.clear();
                }
                // Handle Axios-specific error
                return { error: true, data: error };
            } else {
                // Handle non-Axios errors
                return { error: true, data: 'An unexpected error occurred' };
            }
        }
    };    return { getAPI, PostAPI, PatchAPI, PutAPI };
};

export default useApi;